/**
 * CustomerFeedbackPage — Phase 34.10.
 *
 * All customer feedback with optional sentiment filter. Polls every 30 s.
 */

import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';
import { fmtTime } from './CustomersDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface FeedbackEntry {
  id: string;
  customer_id: string;
  trigger_type: string;
  ticket_id: string | null;
  nps_score: number | null;
  response_text: string | null;
  sent_at: number;
  responded_at: number | null;
  language: string;
  sentiment: string | null;
  themes: string[];
  action_required: boolean;
}

interface ListFeedbackResponse {
  feedback: FeedbackEntry[];
  total: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchFeedback(sentiment?: string): Promise<ListFeedbackResponse> {
  const token = await getAccessToken();
  const qs = sentiment ? `?sentiment=${encodeURIComponent(sentiment)}` : '';
  const resp = await fetch(`${apiBaseUrl()}/feedback${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<ListFeedbackResponse>;
}

function useFeedback(sentiment?: string) {
  return useQuery({
    queryKey: ['feedback', sentiment ?? 'all'],
    queryFn: () => fetchFeedback(sentiment),
    refetchInterval: 30_000,
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#34d399',
  neutral:  '#7dd3fc',
  negative: '#f87171',
};

export function sentimentColor(s: string | null): string {
  return s ? (SENTIMENT_COLORS[s] ?? 'var(--omega-ink-subtle)') : 'var(--omega-ink-subtle)';
}

export function npsColor(n: number): string {
  if (n >= 9) return '#34d399';
  if (n >= 7) return '#7dd3fc';
  if (n >= 0) return '#fbbf24';
  return '#f87171';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FilterTab({ label, value, current, onSelect }: {
  label: string;
  value: string;
  current: string;
  onSelect: (v: string) => void;
}) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      style={{
        background: active ? 'rgba(125,211,252,0.12)' : 'rgba(255,255,255,0.05)',
        border: active ? '1px solid rgba(125,211,252,0.35)' : '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
        borderRadius: 4,
        color: active ? '#7dd3fc' : 'var(--omega-ink)',
        fontFamily: 'var(--omega-mono)',
        fontSize: '8.5pt',
        fontWeight: 600,
        padding: '5px 12px',
        cursor: 'pointer',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </button>
  );
}

function FeedbackRow({ f }: { f: FeedbackEntry }) {
  return (
    <div
      style={{
        padding: '12px 0',
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        {f.sentiment && (
          <span style={{ fontSize: '7.5pt', padding: '1px 6px', border: `1px solid ${sentimentColor(f.sentiment)}`, color: sentimentColor(f.sentiment), letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3 }}>
            {f.sentiment}
          </span>
        )}
        {f.nps_score !== null && (
          <span style={{ fontSize: '9pt', fontWeight: 700, color: npsColor(f.nps_score) }}>
            NPS {f.nps_score}
          </span>
        )}
        <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {f.trigger_type}
        </span>
        {f.action_required && (
          <span style={{ fontSize: '7.5pt', padding: '1px 6px', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24', borderRadius: 3, fontWeight: 700 }}>
            ACTION REQUIRED
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
          {fmtTime(f.sent_at)}
        </span>
      </div>

      {f.themes.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {f.themes.map((theme, i) => (
            <span key={i} style={{ fontSize: '7.5pt', padding: '1px 6px', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))', borderRadius: 3, color: 'var(--omega-ink-subtle)' }}>
              {theme}
            </span>
          ))}
        </div>
      )}

      {f.response_text && (
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          "{f.response_text.slice(0, 120)}{f.response_text.length > 120 ? '…' : ''}"
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function CustomerFeedbackPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [searchParams, setSearchParams] = useSearchParams();
  const sentimentFilter = searchParams.get('sentiment') ?? 'all';

  function setSentiment(s: string) {
    setSearchParams(s === 'all' ? {} : { sentiment: s });
  }

  const { data, isPending, isError } = useFeedback(
    sentimentFilter === 'all' ? undefined : sentimentFilter
  );
  const feedback = data?.feedback ?? [];
  const actionRequired = feedback.filter(f => f.action_required).length;

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Customers', to: '/omega/customers' },
          { label: 'Feedback' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Customer Feedback
        </h1>
        {actionRequired > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 10,
              background: '#fbbf24',
              color: '#0a0d12',
              fontSize: '8pt',
              fontWeight: 700,
            }}
          >
            {actionRequired}
          </span>
        )}
        <span style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)' }}>
          {data?.total ?? 0} total
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'positive', 'neutral', 'negative'].map(s => (
          <FilterTab
            key={s}
            label={s.charAt(0).toUpperCase() + s.slice(1)}
            value={s}
            current={sentimentFilter}
            onSelect={setSentiment}
          />
        ))}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load feedback.</div>}
      {!isPending && !isError && feedback.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
          No feedback yet.
        </div>
      )}
      {feedback.map(f => <FeedbackRow key={f.id} f={f} />)}
    </div>
  );
}
